import { DocsService } from '@affine/core/modules/doc';
import { MarkdownAdapter } from '@blocksuite/affine/shared/adapters';
import { useService } from '@toeverything/infra';
import { debounce } from 'lodash-es';
import { useEffect, useState } from 'react';

import { type BacklinkGroup, renderBacklinkBlock } from './backlink-utils';

export function useBacklinkPreviews(group: BacklinkGroup, enabled: boolean) {
  const docsService = useService(DocsService);
  const [previews, setPreviews] = useState<{
    docId: string;
    markdown: Map<string, string | undefined>;
  }>();
  // Index revalidation can replace the group without changing its blocks.
  const blockIdsKey = JSON.stringify(group.links.map(link => link.blockId));

  useEffect(() => {
    if (!enabled) return;
    let disposed = false;
    let revision = 0;
    let release: (() => void) | undefined;
    let unsubscribe: (() => void) | undefined;
    let disposeTransformer: (() => void) | undefined;
    let adapter: MarkdownAdapter | undefined;
    const blockIds: string[] = JSON.parse(blockIdsKey);
    setPreviews(undefined);

    const load = async () => {
      const ref = docsService.open(group.docId);
      let released = false;
      release = () => {
        if (released) return;
        released = true;
        ref.release();
      };
      const { doc } = ref;

      const refresh = async () => {
        if (!adapter || disposed) return;
        const currentAdapter = adapter;
        const currentRevision = ++revision;
        const markdown = await Promise.all(
          blockIds.map(async blockId => {
            let preview: string | undefined;
            try {
              preview = await renderBacklinkBlock(
                doc.blockSuiteDoc,
                blockId,
                currentAdapter
              );
            } catch {
              // Keep the indexed plain-text fallback when a block cannot be
              // serialized (for example, a missing extension or deleted block).
            }
            return [blockId, preview] as const;
          })
        );
        if (!disposed && currentRevision === revision) {
          setPreviews({ docId: group.docId, markdown: new Map(markdown) });
        }
      };
      const scheduledRefresh = debounce(() => void refresh(), 100, {
        maxWait: 500,
      });
      const onTransaction = () => {
        revision++;
        scheduledRefresh();
      };
      doc.yDoc.on('afterTransaction', onTransaction);
      unsubscribe = () => {
        scheduledRefresh.cancel();
        doc.yDoc.off('afterTransaction', onTransaction);
      };

      await doc.waitForSyncReady();
      if (disposed) return;
      const store = doc.blockSuiteDoc;
      const transformer = store.getTransformer([
        ({ adapterConfigs }) => {
          adapterConfigs.set(
            'docLinkBaseUrl',
            `/workspace/${store.workspace.id}`
          );
        },
      ]);
      disposeTransformer = () => transformer[Symbol.dispose]();
      adapter = new MarkdownAdapter(transformer, store.provider);
      await refresh();
    };

    void load().catch(() => {
      unsubscribe?.();
      unsubscribe = undefined;
      disposeTransformer?.();
      disposeTransformer = undefined;
      release?.();
      release = undefined;
    });

    return () => {
      disposed = true;
      unsubscribe?.();
      disposeTransformer?.();
      release?.();
    };
  }, [docsService, group.docId, blockIdsKey, enabled]);

  return enabled && previews?.docId === group.docId
    ? previews.markdown
    : undefined;
}
